import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Users as UsersIcon, Bell, Settings, Layout, History as HistoryIcon, LogOut, Activity, ShieldAlert } from 'lucide-react';
import { useAuth, RequireAdmin } from './utils/AuthProvider';
import Dashboard from './pages/Dashboard';
import UsersList from './pages/Users';
import History from './pages/History';
import AlertTypes from './pages/AlertTypes';
import ConnectionTest from './pages/ConnectionTest';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import Unauthorized from './pages/Unauthorized';
import UserDashboard from './pages/UserDashboard';

function Navigation() {
  const location = useLocation();
  const { signOut, isAdmin } = useAuth();

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const getLinkClasses = (path: string) => {
    return `${
      isActiveRoute(path)
        ? 'border-indigo-500 text-gray-900'
        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`;
  };

  return (
    <nav className="bg-white border-b">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Bell className="h-8 w-8 text-indigo-600" />
              <span className="ml-2 text-xl font-bold">Webhook Notifier</span>
              {isAdmin && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  <ShieldAlert className="h-3 w-3 mr-1" />
                  Admin
                </span>
              )}
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <Link to="/" className={getLinkClasses('/')}>
                <Layout className="h-4 w-4 mr-2" />
                Dashboard
              </Link>
              
              {isAdmin && (
                <>
                  <Link to="/users" className={getLinkClasses('/users')}>
                    <UsersIcon className="h-4 w-4 mr-2" />
                    Users
                  </Link>
                  <Link to="/history" className={getLinkClasses('/history')}>
                    <HistoryIcon className="h-4 w-4 mr-2" />
                    History
                  </Link>
                  <Link to="/alert-types" className={getLinkClasses('/alert-types')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Alert Types
                  </Link>
                  <Link to="/connection" className={getLinkClasses('/connection')}>
                    <Activity className="h-4 w-4 mr-2" />
                    Connection
                  </Link>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center">
            <button
              onClick={() => signOut()}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 hover:text-gray-700 focus:outline-none transition"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}

function AuthenticatedApp() {
  const { isAdmin } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <Routes>
          {/* Routes accessible by all authenticated users */}
          <Route path="/" element={isAdmin ? <Dashboard /> : <UserDashboard />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          
          {/* Admin-only routes */}
          <Route path="/users" element={
            <RequireAdmin>
              <UsersList />
            </RequireAdmin>
          } />
          <Route path="/history" element={
            <RequireAdmin>
              <History />
            </RequireAdmin>
          } />
          <Route path="/alert-types" element={
            <RequireAdmin>
              <AlertTypes />
            </RequireAdmin>
          } />
          <Route path="/connection" element={
            <RequireAdmin>
              <ConnectionTest />
            </RequireAdmin>
          } />
          {/* 404 catch-all route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  const { session } = useAuth();

  return (
    <Router>
      <Routes>
        <Route path="/login" element={!session ? <Login /> : <Navigate to="/" />} />
        <Route
          path="/*"
          element={session ? <AuthenticatedApp /> : <Navigate to="/login" />}
        />
      </Routes>
    </Router>
  );
}

export default App;